import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
// The same pill and switch as the auto-advance control beside it, so the
// toolbar has one look for a switch. No new styles, and the dark rules come
// with it.
import "../AutoAdvanceToggle/AutoAdvanceToggle.scss";
import "./runningNotice.scss";

/**
 * With the switch on, the orders page shows EVERY running order on one page,
 * with no page buttons (the owner, 2026-09-27). Running orders are few: the
 * production badge said «جارية 7» that day. This ceiling only protects the
 * browser if something goes wrong. The API has no maximum page size.
 */
export const RUNNING_ORDERS_PAGE_SIZE = 1000;

/** The URL filters that turn the running default off. See isRunningOnly. */
const BILLING_OR_STATUS_FILTERS = ["status", "vendor", "delivery", "startDate", "endDate"];

/** Where the browser keeps the operator's last choice of the switch. */
const RUNNING_CHOICE_KEY = "ordersRunningOnly";

/**
 * The operator's last choice of the switch, kept in this browser.
 *
 * `localStorage` can be blocked or can throw (a private window), so a failed
 * read means "no choice", and the page uses its default.
 *
 * @returns {boolean|null} null when the operator never used the switch
 */
export const readRunningChoice = () => {
    try {
        const stored = window.localStorage.getItem(RUNNING_CHOICE_KEY);
        if (stored === "1") return true;
        if (stored === "0") return false;
    } catch {
        // No storage: no choice.
    }
    return null;
};

/**
 * Keeps the choice, so the page opens the same way after a reload, and after
 * the sidebar link opens the plain `/orders` (the owner, 2026-10-01).
 *
 * @param {boolean} runningOnly
 */
export const storeRunningChoice = (runningOnly) => {
    try {
        window.localStorage.setItem(RUNNING_CHOICE_KEY, runningOnly ? "1" : "0");
    } catch {
        // No storage: the choice lasts as long as the URL does.
    }
};

/**
 * Whether the page shows only running orders.
 *
 * `?running=1` and `?running=0` always win, so the switch is explicit in both
 * directions.
 *
 * With no `running` in the URL, the page uses the operator's last choice
 * (readRunningChoice). An operator who never used the switch gets it ON (the
 * owner, 2026-09-28): the orders page opens on the running orders.
 *
 * With no `running` in the URL, any of these filters turns it off:
 * - a status: «اكتمل» and «running only» together show an empty page;
 * - a restaurant, a driver or a date range: that is the billing view.
 *   «إرسال كشف حساب» and «حذف طلبات المطعم» act on the FINISHED orders, and
 *   the cards («الطلبات المحتسبة», «الإجمالي المحتسب») must count the same
 *   rows. With running on, the screen would show the opposite set.
 *
 * @param {URLSearchParams} searchParams
 * @returns {boolean}
 */
export const isRunningOnly = (searchParams) => {
    const running = searchParams.get("running");
    if (running === "1") return true;
    if (running === "0") return false;
    if (BILLING_OR_STATUS_FILTERS.some((key) => searchParams.get(key))) return false;
    return readRunningChoice() ?? true;
};

/**
 * «عرض الطلبات الجارية فقط»: the orders page shows only the orders that the
 * sidebar's «جارية» badge counts.
 *
 * The dashboard does not keep a list of statuses for this. It sends
 * `inProgress=true`, and the API applies IN_PROGRESS_ORDER_STATUSES from
 * api/src/util/orderListMetrics.js, the list the badge counts. So the button
 * and the badge cannot disagree.
 *
 * @param {{checked: boolean, onChange: (next: boolean) => void}} props
 */
const RunningOrdersToggle = ({ checked, onChange }) => (
    <div
        className={`auto-advance ${checked ? "auto-advance--on" : "auto-advance--off"}`}
        title="يعرض الطلبات التي لم تنتهِ بعد، نفس طلبات «جارية» في القائمة الجانبية"
    >
        <FontAwesomeIcon icon={faClock} className="auto-advance__icon" />
        <label htmlFor="running-orders-only" className="auto-advance__label">
            عرض الطلبات الجارية فقط
        </label>
        <button
            id="running-orders-only"
            type="button"
            role="switch"
            aria-checked={checked}
            className="auto-advance__switch"
            onClick={() => onChange(!checked)}
        >
            <span className="auto-advance__thumb" />
        </button>
    </div>
);

export default RunningOrdersToggle;

/**
 * The line above the order cards while the switch is on: the page and the
 * figures under it count only running orders, and one click shows them all.
 * Without it, «عدد الطلبات 19» reads as if the restaurant had 19 orders in all.
 *
 * @param {{onShowAll: () => void}} props
 */
export const RunningOrdersNotice = ({ onShowAll }) => (
    <div className="running-notice" role="status">
        <FontAwesomeIcon icon={faClock} className="running-notice__icon" />
        <span className="running-notice__text">
            تعرض الصفحة والأرقام تحتها الطلبات الجارية فقط.
        </span>
        <button type="button" className="running-notice__action" onClick={onShowAll}>
            عرض كل الطلبات
        </button>
    </div>
);
